import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { getCurrentUser, isAllBranchesRole } from '../utils/auth';
import { useToast } from './ToastContext';
import { useAutoSync } from '../hooks/useAutoSync';

const ScrollHintIcon = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 5l7 7m0 0l-7 7m7-7H3" />
  </svg>
);

function Summary({ companyId }) {
  const currentUser = getCurrentUser();
  const canSelectAllBranches = isAllBranchesRole(currentUser?.role);
  const [summaryData, setSummaryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage] = useState(1000);
  const [gotoPageInput, setGotoPageInput] = useState('');
  const [recordsCached, setRecordsCached] = useState(0);
  const [lastSyncAt, setLastSyncAt] = useState(null);
  
  // Toast notifications
  const { showToast } = useToast();
  
  // Auto-sync monitoring
  useAutoSync(true);

  // Date selection
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().slice(0, 10));
  const selectedMonth = selectedDate.slice(0, 7);

  // Branch filter
  const ALL_BRANCHES_VALUE = '';
  const [selectedBranch, setSelectedBranch] = useState(companyId ? String(companyId) : '');
  const [companies, setCompanies] = useState([]);

  useEffect(() => {
    fetchSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, selectedBranch]);

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      const response = await api.get('/companies');
      setCompanies(response.data || []);
    } catch (err) {
      console.error('Failed to fetch companies:', err);
    }
  };

  const getCompanyIdParam = () => {
    if (!canSelectAllBranches && !selectedBranch) return companyId || '';
    return selectedBranch === ALL_BRANCHES_VALUE ? '' : selectedBranch;
  };

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const companyIdParam = getCompanyIdParam();
      const params = new URLSearchParams();
      // Use month parameter for monthly summary (aggregated data)
      params.append('month', selectedMonth);
      if (companyIdParam) params.append('company_id', companyIdParam);
      const response = await api.get('/reports/manual-summary?' + params.toString());
      setSummaryData(response.data);
      setRecordsCached(response.data.length);
      if (response.data.length > 0 && response.data[0].last_sync_at) {
        setLastSyncAt(response.data[0].last_sync_at);
      } else {
        setLastSyncAt(null);
      }
    } catch (err) {
      setMessage('Failed to load summary data.');
    } finally {
      setLoading(false);
    }
  };

  const refreshCache = async () => {
    setLoading(true);
    setMessage('');
    try {
      // Use month parameter for monthly summary refresh
      await api.post('/reports/manual-summary/refresh', { month: selectedMonth });
      setMessage('Summary cache refreshed successfully.');
      await fetchSummary();
    } catch (err) {
      setMessage('Failed to refresh summary cache.');
    } finally {
      setLoading(false);
    }
  };

  // Filter data based on search term
  const getFilteredData = () => {
    return summaryData.filter(row => {
      const matchesSearch = !searchTerm ||
        row.company_name?.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesSearch;
    });
  };

  const filteredData = getFilteredData();

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filteredData.length / entriesPerPage));
  const startIndex = (currentPage - 1) * entriesPerPage;
  const endIndex = startIndex + entriesPerPage;
  const paginatedData = filteredData.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    const p = Math.min(Math.max(1, page), totalPages);
    setCurrentPage(p);
    setGotoPageInput('');
  };

  const handleGotoPage = () => {
    const page = parseInt(gotoPageInput, 10);
    if (!Number.isNaN(page)) {
      handlePageChange(page);
    }
  };

  const handleGotoPageKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleGotoPage();
    }
  };

  // Calculate totals
  const calculateTotals = () => {
    return filteredData.reduce((acc, row) => ({
      total_collectibles: acc.total_collectibles + parseFloat(row.total_collectibles || 0),
      total_collection_today: acc.total_collection_today + parseFloat(row.total_collection_today || 0),
      posted_collected: acc.posted_collected + parseFloat(row.posted_collected || 0),
      on_dated_cheque: acc.on_dated_cheque + parseFloat(row.on_dated_cheque || 0),
      pdc: acc.pdc + parseFloat(row.pdc || 0),
      mtd: acc.mtd + parseFloat(row.mtd || 0),
      overall_total_collected: acc.overall_total_collected + parseFloat(row.overall_total_collected || 0),
      ce_percentage: acc.ce_percentage + parseFloat(row.ce_percentage || 0),
      remaining_collectibles: acc.remaining_collectibles + parseFloat(row.remaining_collectibles || 0),
    }), {
      total_collectibles: 0,
      total_collection_today: 0,
      posted_collected: 0,
      on_dated_cheque: 0,
      pdc: 0,
      mtd: 0,
      overall_total_collected: 0,
      ce_percentage: 0,
      remaining_collectibles: 0,
    });
  };

  const totals = calculateTotals();

  const formatCurrency = (value) => {
    if (value === undefined || value === null) return '0.00';
    const num = parseFloat(value);
    if (Number.isNaN(num)) return '0.00';
    return num.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatDateMonthYear = (dateStr) => {
    const date = new Date(dateStr + '-01T00:00:00');
    return date.toLocaleDateString('en-PH', { year: 'numeric', month: 'long' });
  };

  return (
    <div className="summary-container">
      {/* Page Header */}
      <div className="page-header">
        <h1>Summary Branch</h1>
        <button className="btn btn-primary" onClick={refreshCache} style={{ backgroundColor: '#e63740', borderColor: '#e63740' }}>
          {loading ? 'Refreshing...' : 'Refresh Cache'}
        </button>
      </div>

      {/* Success/Error Message */}
      {message && (
        <div className={`alert ${message.includes('success') ? 'alert-success' : 'alert-error'}`}>
          {message}
        </div>
      )}

      {/* Cache Status */}
      <div className="cache-status">
        <span>
          <strong>Cache Status:</strong>{' '}
          {recordsCached > 0 ? (
            <>
              {recordsCached} records cached
              {lastSyncAt && (
                <> · Last synced: {new Date(lastSyncAt).toLocaleString()}</>
              )}
            </>
          ) : (
            'No cached data. Click Refresh Cache to sync.'
          )}
        </span>
      </div>

      {/* Branch, Date and Search Filters */}
      <div className="card report-filters" style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '15px' }}>
        {canSelectAllBranches && (
          <select
            value={selectedBranch}
            onChange={(e) => { setSelectedBranch(e.target.value); setCurrentPage(1); }}
            className="form-control"
            style={{ width: '180px' }}
          >
            <option value={ALL_BRANCHES_VALUE}>All Branches</option>
            {companies.map((company) => (
              <option key={company.id} value={String(company.id)}>
                {company.name}
              </option>
            ))}
          </select>
        )}

        <label htmlFor="date-select" style={{ marginRight: '0px', fontWeight: '600' }}>
          As of Date:
        </label>
        <input
          type="date"
          id="date-select"
          value={selectedDate}
          onChange={(e) => {
            setSelectedDate(e.target.value);
            setCurrentPage(1);
          }}
          className="form-control"
          style={{ width: '160px' }}
        />

        <input
          type="text"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
          className="form-control search-input"
          style={{ width: '160px', maxWidth: '100%', minWidth: '120px' }}
        />
      </div>

      {loading ? (
        <div className="loading">Loading summary data...</div>
      ) : filteredData.length === 0 ? (
        <div className="empty-state">No summary data available for this period.</div>
      ) : (
        <>
          <div className="card ar-table-card summary-table-card">
            <div className="table-scroll-hint">
              {ScrollHintIcon}
              <span>Swipe or scroll horizontally to see all columns</span>
            </div>

            {/* Grand Summary Card */}
            <div className="grand-total-card summary-grand-total">
              <div className="summary-as-of">
                As of {formatDateMonthYear(selectedMonth)}
              </div>
              <div className="grand-total-grid">
                <div className="grand-total-item">
                  <span className="label">Total Collectibles</span>
                  <span className="value">{formatCurrency(totals.total_collectibles)}</span>
                </div>
                <div className="grand-total-item">
                  <span className="label">Total Collection as of Today (Posted)</span>
                  <span className="value">{formatCurrency(totals.amount_collected)}</span>
                </div>
                <div className="grand-total-item">
                  <span className="label">Posted Collected (ERP)</span>
                  <span className="value">{formatCurrency(totals.posted_collected)}</span>
                </div>
                <div className="grand-total-item">
                  <span className="label">On-Dated Cheque</span>
                  <span className="value">{formatCurrency(totals.on_dated_cheque)}</span>
                </div>
                <div className="grand-total-item">
                  <span className="label">PDC</span>
                  <span className="value">{formatCurrency(totals.pdc)}</span>
                </div>
                <div className="grand-total-item">
                  <span className="label">MTD Total Collected</span>
                  <span className="value">{formatCurrency(totals.mtd)}</span>
                </div>
                <div className="grand-total-item highlight">
                  <span className="label">Overall Total Collected</span>
                  <span className="value">{formatCurrency(totals.overall_total_collected)}</span>
                </div>
                <div className="grand-total-item percentage">
                  <span className="label">CE% ftm</span>
                  <span className="value">
                    {totals.total_collectibles > 0 
                      ? ((totals.overall_total_collected / totals.total_collectibles) * 100).toFixed(0) + '%' 
                      : '0%'}
                  </span>
                </div>
                <div className="grand-total-item">
                  <span className="label">Remaining Collectibles</span>
                  <span className="value">{formatCurrency(totals.remaining_collectibles)}</span>
                </div>
              </div>
            </div>

            {/* Data Table */}
            <table className="table ar-table summary-data-table table-compact">
              <thead>
                <tr>
                  <th className="sp-col" style={{ backgroundColor: '#e63740', color: '#ffffff' }}>Company/Branch</th>
                  <th className="number-col number-highlight total-collectibles-col" style={{ backgroundColor: '#e63740', color: '#ffffff' }}>Total Collectible</th>
                  <th className="number-col number-highlight" style={{ backgroundColor: '#e63740', color: '#ffffff' }}>Total Collection as of Today (Posted)</th>
                  <th className="number-col number-highlight" style={{ backgroundColor: '#e63740', color: '#ffffff' }}>Posted Collected (ERP)</th>
                  <th className="number-col" style={{ backgroundColor: '#e63740', color: '#ffffff' }}>On-Dated Cheque</th>
                  <th className="number-col" style={{ backgroundColor: '#e63740', color: '#ffffff' }}>PDC</th>
                  <th className="number-col" style={{ backgroundColor: '#e63740', color: '#ffffff' }}>MTD - {new Date(selectedMonth + '-01').toLocaleDateString('en-PH', { month: 'short' }).toUpperCase()}</th>
                  <th className="number-col number-highlight" style={{ backgroundColor: '#e63740', color: '#ffffff' }}>Overall Total Collected</th>
                  <th className="percentage-col" style={{ backgroundColor: '#e63740', color: '#ffffff' }}>CE% ftm {new Date(selectedMonth + '-01').toLocaleDateString('en-PH', { month: 'short' }).toUpperCase()}</th>
                  <th className="number-col number-highlight" style={{ backgroundColor: '#e63740', color: '#ffffff' }}>Remaining Collectibles as of Today</th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((row, index) => (
                  <tr key={row.company_id || index}>
                    <td><strong>{row.company_name || '-'}</strong></td>
                    <td className="number-col total-collectibles-col">{formatCurrency(row.total_collectibles)}</td>
                    <td className="number-col">{formatCurrency(row.amount_collected || 0)}</td>
                    <td className="number-col">{formatCurrency(row.posted_collected)}</td>
                    <td className="number-col">{formatCurrency(row.on_dated_cheque)}</td>
                    <td className="number-col">{formatCurrency(row.pdc)}</td>
                    <td className="number-col">{formatCurrency(row.mtd)}</td>
                    <td className="number-col"><strong>{formatCurrency(row.overall_total_collected)}</strong></td>
                    <td className="percentage-col">
                      <span className={`percentage-value ${(row.ce_percentage || 0) >= 60 ? 'success' : (row.ce_percentage || 0) > 0 ? 'warning' : 'danger'}`}>
                        {(row.ce_percentage || 0).toFixed(0)}%
                      </span>
                    </td>
                    <td className="number-col">{formatCurrency(row.remaining_collectibles)}</td>
                  </tr>
                ))}
                
                {/* Total Row */}
                <tr className="total-row">
                  <td><strong>GRAND TOTAL</strong></td>
                  <td className="number-col total-collectibles-col">{formatCurrency(totals.total_collectibles)}</td>
                  <td className="number-col">{formatCurrency(totals.amount_collected)}</td>
                  <td className="number-col">{formatCurrency(totals.posted_collected)}</td>
                  <td className="number-col">{formatCurrency(totals.on_dated_cheque)}</td>
                  <td className="number-col">{formatCurrency(totals.pdc)}</td>
                  <td className="number-col">{formatCurrency(totals.mtd)}</td>
                  <td className="number-col"><strong>{formatCurrency(totals.overall_total_collected)}</strong></td>
                  <td className="percentage-col">
                    {totals.total_collectibles > 0 
                      ? ((totals.overall_total_collected / totals.total_collectibles) * 100).toFixed(0) + '%' 
                      : '0%'}
                  </td>
                  <td className="number-col">{formatCurrency(totals.remaining_collectibles)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

export default Summary;
